import React, { useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, Legend } from 'recharts';
import { Button, Space } from 'antd';

const UserBreakdownCard = ({ 
  data = [], 
  loading = false 
}) => {
  const [viewMode, setViewMode] = useState('chart'); // 'chart' or 'table'
  // Status colors for different outbound call statuses
  // Supports both dash and underscore formats for backward compatibility
  const getStatusColor = (status) => {
    // Normalize status to underscore format
    const normalizedStatus = status ? status.replace(/-/g, '_').replace(/\//g, '_') : '';
    
    const colors = {
      'completed': '#22c55e', // green
      // New underscore format from RPC
      'busy_no_answer': '#f59e0b', // amber (combining busy + no_answer)
      'in_progress_ringing': '#3b82f6', // blue
      // Legacy formats for backward compatibility
      'busy/no_answer': '#f59e0b',
      'busy/no answer': '#f59e0b',
      'in_progress/ringing': '#3b82f6',
      'in-progress/ringing': '#3b82f6',
      'no_answer': '#f59e0b',
      'busy': '#f59e0b',
      'in_progress': '#3b82f6',
      'ringing': '#3b82f6',
      // Other statuses
      'failed': '#dc2626',    // red-600
      'canceled': '#9ca3af',  // gray-400
      'cancelled': '#9ca3af', // alternate spelling
      'missed_inbound': '#ea580c', // orange-600
      'missed': '#dc2626'     // red-600
    };
    return colors[status] || colors[normalizedStatus] || '#6b7280';
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Custom tick formatter to handle long names and include user ID
  const formatUserName = (name) => {
    if (!name) return 'Unknown';
    
    // Find the user data to get the user ID
    const userData = data.find(user => user.userName === name);
    const userId = userData ? userData.userId : '';
    
    // Special handling for AI_RIA
    if (name === 'AI_RIA' || userData?.isRia) {
      return '🤖 AI_RIA';
    }
    
    const displayName = `${name} (${userId})`;
    
    if (displayName.length > 25) {
      return displayName.substring(0, 22) + '...';
    }
    return displayName;
  };

  // Calculate dynamic height based on number of users - increased heights
  const calculateHeight = () => {
    const baseHeight = 500;
    const heightPerUser = data.length > 20 ? 90 : data.length > 15 ? 85 : data.length > 10 ? 80 : 75;
    const padding = 150;
    const calculatedHeight = Math.max(baseHeight, data.length * heightPerUser + padding);
    
    return calculatedHeight;
  };

  // Calculate dynamic spacing based on number of users
  const getChartMargins = () => {
    // Balance left and right margins to center the chart better
    // Increased left margin to accommodate wider Y-axis labels
    const baseMargin = { top: 20, right: 150, left: 100, bottom: 20 };
    if (data.length > 10) {
      // For many users, add more top/bottom margin and balance sides
      return { ...baseMargin, top: 30, bottom: 30, right: 110, left: 60 };
    }
    return { ...baseMargin, right: 100, left: 220 }; // Better balance for fewer users with more label space
  };

  // Render table view
  const renderTableView = () => {
    return (
      <div style={{ 
        maxHeight: '600px', 
        overflowY: 'auto',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        marginTop: '16px'
      }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          fontSize: '14px'
        }}>
          <thead style={{ 
            backgroundColor: '#f9fafb',
            position: 'sticky',
            top: 0,
            zIndex: 1
          }}>
            <tr>
              <th style={{ 
                padding: '12px 16px', 
                textAlign: 'left', 
                borderBottom: '2px solid #d1d5db',
                fontWeight: '600'
              }}>
                User
              </th>
              <th style={{ 
                padding: '12px 16px', 
                textAlign: 'center', 
                borderBottom: '2px solid #d1d5db',
                fontWeight: '600'
              }}>
                Total Calls
              </th>
              <th style={{ 
                padding: '12px 16px', 
                textAlign: 'center', 
                borderBottom: '2px solid #d1d5db',
                fontWeight: '600'
              }}>
                Avg Duration
              </th>
              <th style={{ 
                padding: '12px 16px', 
                textAlign: 'left', 
                borderBottom: '2px solid #d1d5db',
                fontWeight: '600'
              }}>
                Status Breakdown
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((user, index) => (
              <tr key={user.userId} style={{ 
                backgroundColor: user.isRia ? 'rgba(230, 92, 0, 0.05)' : (index % 2 === 0 ? '#ffffff' : '#f9fafb'),
                borderBottom: '1px solid #e5e7eb'
              }}>
                <td style={{ 
                  padding: '12px 16px',
                  fontWeight: '500'
                }}>
                  {user.isRia || user.userName === 'AI_RIA' ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '2px 8px',
                        background: '#e65c00',
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        borderRadius: '4px'
                      }}>
                        🤖 AI_RIA
                      </span>
                    </span>
                  ) : (
                    <>{user.userName} ({user.userId})</>
                  )}
                </td>
                <td style={{ 
                  padding: '12px 16px', 
                  textAlign: 'center',
                  fontWeight: '600'
                }}>
                  {user.totalCalls}
                </td>
                <td style={{ 
                  padding: '12px 16px', 
                  textAlign: 'center'
                }}>
                  {formatDuration(user.avgDuration)}
                </td>
                <td style={{ 
                  padding: '12px 16px'
                }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {Object.entries(user.statusCounts || {})
                      .sort(([,a], [,b]) => b - a) // Sort by count descending
                      .map(([status, count]) => (
                        <span key={status} style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '11px',
                          backgroundColor: getStatusColor(status) + '20',
                          color: getStatusColor(status),
                          border: `1px solid ${getStatusColor(status)}40`,
                          textTransform: 'capitalize'
                        }}>
                          {status.replace(/_/g, ' ').replace(/-/g, ' ')}: {count}
                        </span>
                      ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="analytics-chart-card" style={{ 
      gridColumn: '1 / -1', 
      marginTop: '24px',
      padding: '24px'
    }}>
      <div className="analytics-chart-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <svg className="analytics-chart-icon" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
          <div className="analytics-chart-title">Outbound Calls by User</div>
        </div>
        <Space.Compact size="small">
          <Button 
            type={viewMode === 'chart' ? 'primary' : 'default'}
            onClick={() => setViewMode('chart')}
            icon={
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22 21H2l4-7 4 7 4-7 4 7 4-7z"/>
              </svg>
            }
          >
            Chart
          </Button>
          <Button 
            type={viewMode === 'table' ? 'primary' : 'default'}
            onClick={() => setViewMode('table')}
            icon={
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                <path d="M10 10.02h5V21h-5zM17 21h5V10.02h-5zM3 13.51h5V21H3zM3 3v8.51h5V3zm7 0v8.51h5V3zm7 0v8.51h5V3z"/>
              </svg>
            }
          >
            Table
          </Button>
        </Space.Compact>
      </div>
      <div className="analytics-chart-metric">
        <div className="analytics-chart-value">
          {loading ? '...' : `${data.length} users`}
        </div>
        <div className="analytics-chart-period">
          {viewMode === 'chart' ? 'Call breakdown with status' : 'Detailed breakdown table'}
        </div>
      </div>
      
      {/* Conditional rendering based on view mode */}
      {viewMode === 'chart' ? (
        <div className="analytics-chart-container" style={{ 
          height: calculateHeight() + 'px',
          minHeight: '500px'
        }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={data} 
              layout="vertical"
              margin={getChartMargins()}
            >
              <XAxis 
                type="number"
                tick={{ fontSize: 12, fill: '#666' }} 
                axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }} 
                tickLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
              />
              <YAxis 
                type="category"
                dataKey="userName"
                tick={{ 
                  fontSize: 11, 
                  fill: '#666', 
                  textAnchor: 'end',
                  width: 190,
                  wordWrap: false
                }} 
                axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }} 
                tickLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                width={195}
                interval={0}
                tickFormatter={formatUserName}
              />
              <Tooltip 
                formatter={(value, name, props) => {
                  // Convert underscore format to readable format
                  const statusName = name.replace(/_/g, ' ').replace(/-/g, ' ');
                  return [
                    `${value} calls`,
                    statusName.charAt(0).toUpperCase() + statusName.slice(1)
                  ];
                }}
                labelFormatter={(label) => `User: ${label} (Total: ${data.find(d => d.userName === label)?.totalCalls || 0} calls, Avg: ${formatDuration(data.find(d => d.userName === label)?.avgDuration || 0)})`}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
              />
              {/* Stacked bars for each outbound call status (underscore format) */}
              <Bar dataKey="completed" stackId="status" fill={getStatusColor('completed')} radius={[0, 0, 0, 0]} />
              <Bar dataKey="busy_no_answer" stackId="status" fill={getStatusColor('busy_no_answer')} radius={[0, 0, 0, 0]} />
              <Bar dataKey="in_progress_ringing" stackId="status" fill={getStatusColor('in_progress_ringing')} radius={[0, 0, 0, 0]} />
              <Bar dataKey="failed" stackId="status" fill={getStatusColor('failed')} radius={[0, 0, 0, 0]} />
              <Bar dataKey="canceled" stackId="status" fill={getStatusColor('canceled')} radius={[0, 4, 4, 0]} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                formatter={(value) => {
                  // Convert underscore format to readable format
                  const statusName = value.replace(/_/g, ' ').replace(/-/g, ' ');
                  return statusName.charAt(0).toUpperCase() + statusName.slice(1);
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        renderTableView()
      )}
      
      {/* Legend for status colors - only show in table mode since chart has its own legend */}
      {viewMode === 'table' && (
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '12px', 
          marginTop: '16px', 
          fontSize: '12px',
          justifyContent: 'center'
        }}>
          {['completed', 'busy_no_answer', 'in_progress_ringing', 'failed', 'canceled'].map(status => (
            <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div 
                style={{ 
                  width: '12px', 
                  height: '12px', 
                  backgroundColor: getStatusColor(status),
                  borderRadius: '2px'
                }}
              />
              <span style={{ textTransform: 'capitalize' }}>
                {status.replace(/_/g, ' ')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserBreakdownCard;
